import {
  Canvas,
  getCanvasHeight,
  getCanvasWidth,
  getImperialScaleLabel,
  getScalingFactor,
  getSheetName,
  LengthFormat,
  PageFormat,
  resolveLengthFormat,
  UnitSystem,
} from '@ritabygglov/core';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { canvasService } from '../../apiServices/CanvasService';
import { siteConfig } from '../../config/siteConfig';
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import { useRoomId } from '../../hooks/useRoomId';
import { Flex } from '../Flex/Flex';
import { Sizing } from '../../styles/spacing';

// The title block sits in the bottom-right corner of the sheet, this far in
// from each edge. Exported so other overlays (e.g. NorthArrow) can position
// themselves clear of it instead of guessing.
export const TITLE_BLOCK_X_OFFSET = 400;
export const TITLE_BLOCK_Y_OFFSET = 350;

const Head = styled.div`
  bottom: 0px;
  right: 0px;
  min-width: 16.5rem;
  border-radius: 0rem;
  background-color: white;
`;

const HideTitleBlockContainer = styled.div`
  display: flex;
  color: rgba(4, 109, 243, 0.4);
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #046df3;
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  border: solid 1px black;
  border-top: 0px;
`;

interface TextProps {
  $bold?: boolean;
  $small?: boolean;
}

const textStyles = css<TextProps>`
  margin: 0;
  text-align: right;
  border: none;
  padding: 0.2rem;
  text-transform: ${({ $bold }) => ($bold ? 'uppercase' : 'none')};
  font-weight: ${({ $bold }) => ($bold ? '700' : 'normal')};
  font-size: ${({ $small }) => ($small ? '0.6rem' : '1rem')};
  color: ${({ $small }) => ($small ? 'rgba(0, 0, 0, 0.64)' : 'black')};

  & :last-child {
    background-color: red;
  }
`;

const Text = styled.p<TextProps>`
  ${textStyles}
`;

const TextInput = styled.input<TextProps>`
  ${textStyles}
  border-bottom: none;
  border-right: none;
  border-left: none;

  &:hover {
    background-color: rgba(4, 109, 243, 0.05);
  }
`;

const BlockItem = styled.div`
  border-top: solid 1px black;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  text-align: left;
  font-size: 10px;
  text-transform: uppercase;
  margin-left: 4px;
  margin-top: 2px;
`;

const ScaleBarContainer = styled.div`
  padding: 0.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: right;
`;

const ScaleBar = styled.div`
  border-bottom: solid 1px black;
  border-left: solid 1px black;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  width: 227px; //this width keeps the scalebar correct

  padding: 0;
  margin: 0.2rem;

  text-align: right;

  & div {
    font-size: 0.8rem;
    font-weight: 500;
    border-right: 1px solid black;
    width: 75px;
    padding: 0 0.2rem 0 0;
    text-transform: lowercase !important;
  }
`;

// Each cell represents this many canvas pixels of the fixed-width bar below.
const SCALE_BAR_CELL_PX = 75;
const METRES_PER_FOOT = 0.3048;
const MILLIMETRES_PER_METRE = 1000;

// Snaps a value to ~2 significant figures, so the bar shows a clean round
// number close to (but not exactly) the true physical distance it spans —
// matching the intent of the old hand-picked tables this replaces.
const roundToNiceNumber = (value: number): number => {
  if (value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = Math.pow(10, 1 - exponent);
  return Math.round(value * magnitude) / magnitude;
};

const ScaleBars: React.FC<{
  scale: string;
  pageFormat: PageFormat;
  unitSystem: UnitSystem;
  lengthFormat: LengthFormat;
}> = ({ scale, pageFormat, unitSystem, lengthFormat }) => {
  const format = resolveLengthFormat(unitSystem, lengthFormat);
  const metresPerCell = getScalingFactor(scale, pageFormat) * SCALE_BAR_CELL_PX;
  const valuePerCell =
    unitSystem === 'imperial'
      ? metresPerCell / METRES_PER_FOOT
      : format === 'millimeters'
        ? metresPerCell * MILLIMETRES_PER_METRE
        : metresPerCell;
  const niceValuePerCell = roundToNiceNumber(valuePerCell);
  // Engineering-scale (decimal feet) drawings label their bar "ft"; feet-inch
  // drawings use the tick mark — matches how each convention appears on
  // real site vs. floor plans.
  const unitSuffix =
    unitSystem === 'imperial'
      ? format === 'decimal-feet'
        ? ' ft'
        : "'"
      : format === 'millimeters'
        ? 'mm'
        : 'm';
  const scaleValues = [1, 2, 3].map((multiplier) => {
    const cellValue = Math.round(niceValuePerCell * multiplier * 1000) / 1000;
    return `${cellValue}${unitSuffix}`;
  });

  return (
    <ScaleBarContainer>
      <ScaleBar>
        {scaleValues.map((text, index) => (
          <div key={index}>{text}</div>
        ))}
      </ScaleBar>
    </ScaleBarContainer>
  );
};

export const TitleBlock: React.FC = () => {
  const user = useUser();
  const canvasId = useRoomId();
  const canvas = user.canvases.find((c) => c.canvasId === canvasId);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const toast = useToast();
  const intl = useIntl();
  const isExport = location.pathname.includes('export');
  const format = intl.formatMessage({
    id: `format.short.${getSheetName(canvas?.pageFormat ?? PageFormat.A3_LANDSCAPE)}`,
  });

  const updateCanvas = useCallback(
    (form: Partial<Canvas>) => {
      canvasService
        .editCanvas(canvasId, form)
        .then(() => {
          user.fetchUser();
        })
        .catch((e: Error) => {
          toast.addToast({
            message: e.message,
            type: 'error',
          });
        });
    },
    [canvasId, toast, user]
  );

  const debouncedUpdate = useMemo(
    () => debounce(updateCanvas, 400),
    [updateCanvas]
  );

  useEffect(() => {
    if (canvas && isBootstrapping) {
      const values: Record<string, string> = {
        name: canvas.name,
        drawingHeadType: canvas.drawingHeadType || '',
        drawingHeadPropertyReference: canvas.drawingHeadPropertyReference || '',
        author: canvas.author || '',
        authorExportedAt: canvas.authorExportedAt || '',
      };
      setFieldValues(values);
      setIsBootstrapping(false);
    }
  }, [canvas, isBootstrapping]);

  const onFieldChange = (fieldKey: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldKey]: value }));
    const form: Partial<Canvas> = { [fieldKey]: value };
    debouncedUpdate(form);
  };

  const onHideDrawingHead = () => {
    const form: Partial<Canvas> = {
      showDrawingHead: false,
    };
    canvasService
      .editCanvas(canvasId, form)
      .then(() => {
        toast.addToast({
          message: intl.formatMessage({ id: 'titleBlock.hiddenToast' }),
          type: 'success',
        });
        user.fetchUser();
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  if (!canvas) return;

  const canvasWidth = getCanvasWidth(canvas?.pageFormat);
  const canvasHeight = getCanvasHeight(canvas?.pageFormat);

  return (
    <foreignObject
      x={canvasWidth - TITLE_BLOCK_X_OFFSET}
      y={canvasHeight - TITLE_BLOCK_Y_OFFSET}
      width="360"
      height="332"
      style={{ pointerEvents: 'none' }}
    >
      <Flex
        $flexDirection="column"
        $alignItems="flex-end"
        $justifyContent="flex-end"
        $padding={Sizing.xs}
      >
        {canvas.showDrawingHead && (
          <>
            <Head style={{ pointerEvents: 'auto' }}>
              <Items>
                {siteConfig.titleBlockFields.map((field, index) => (
                  <BlockItem key={field.key}>
                    <Label htmlFor={field.key}>
                      {intl.formatMessage({ id: field.labelKey })}
                    </Label>
                    <TextInput
                      id={field.key}
                      data-title-block-input
                      $bold={index === 0}
                      value={fieldValues[field.key] || ''}
                      onChange={(e) => onFieldChange(field.key, e.target.value)}
                    />
                  </BlockItem>
                ))}
                <BlockItem>
                  <Text>
                    <FormattedMessage
                      id="titleBlock.formatAndScale"
                      values={{
                        format,
                        scale:
                          canvas.unitSystem === 'imperial'
                            ? (getImperialScaleLabel(canvas.drawingHeadScale) ??
                              canvas.drawingHeadScale)
                            : canvas.drawingHeadScale,
                      }}
                    />
                  </Text>
                </BlockItem>
                <BlockItem>
                  <ScaleBars
                    scale={canvas.drawingHeadScale}
                    pageFormat={canvas.pageFormat}
                    unitSystem={canvas.unitSystem}
                    lengthFormat={canvas.lengthFormat}
                  />
                </BlockItem>
                <BlockItem>
                  <Text $small>
                    <FormattedMessage
                      id="titleBlock.createdWith"
                      values={{ appName: siteConfig.appName }}
                    />
                  </Text>
                </BlockItem>
              </Items>
              {!isExport && (
                <HideTitleBlockContainer onClick={onHideDrawingHead}>
                  {intl.formatMessage({ id: 'titleBlock.hideButton' })}
                </HideTitleBlockContainer>
              )}
            </Head>
          </>
        )}
      </Flex>
    </foreignObject>
  );
};
